import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {setActiveTab} from '../redux/slices/appSlice';
import {MIXPEDIA_FREE_FOR_ALL} from '../../backend/planModel';
import {useDesignV2} from '../utils/designV2';

// Analytics only (feature_open.is_goat_feature): which tiles lead to a GOAT
// feature. The tiles themselves carry NO badge — every one of these is at
// least partly open on the free plan (the utilities analyze for free and
// lock the write; For You / Set Planner / Crate Digger give a free taste),
// so the upsell lives inside the feature, on the locked step or the wall,
// not on the door.
const GOAT_FEATURE_KEYS = new Set([
    'smart-recs', 'organize-tracks', 'fix-metadata', 'match-loudness', 'whats-next', 'ai-set', 'automix',
    // For You counts as a GOAT feature only while the plan model gates it.
    ...(MIXPEDIA_FREE_FOR_ALL ? [] : ['feed']),
]);

function CategoryView({category, features: allFeatures}) {
    const activeTab = useSelector((state) => state.app.activeTab);
    // Automix needs the mixing engine on this machine (automix:getStatus, asked
    // at boot) — no engine, no tile.
    const automixAvailable = useSelector((state) => !!state.automix.engine?.available);
    const features = allFeatures.filter((f) => f.key !== 'automix' || automixAvailable);
    const dispatch = useDispatch();
    const v2 = useDesignV2();
    const activeCategory = useSelector((state) => state.app.activeCategory);

    // Find which feature is active within this category
    const activeFeature = features.find(f => f.key === activeTab);

    const openFeature = (feature) => {
        window.electron.analytics.track('feature_open', {
            feature: feature.key,
            category: category.id,
            is_goat_feature: GOAT_FEATURE_KEYS.has(feature.key),
        });
        dispatch(setActiveTab(feature.key));
    };

    // DESIGN V2 — there is no hub. A category with nothing active lands on its
    // first feature (Discover → For You); the sidebar lists the rest. Only the category the user is looking at may do this:
    // all three CategoryViews stay mounted and activeTab is app-wide.
    const firstKey = features[0]?.key;
    useEffect(() => {
        if (!v2 || activeFeature || activeTab || activeCategory !== category.id || !firstKey) return;
        dispatch(setActiveTab(firstKey));
    }, [v2, activeFeature, activeTab, activeCategory, category.id, firstKey, dispatch]);

    if (activeFeature) {
        const Component = activeFeature.component;
        if (v2) {
            // The sidebar (components/v2/SideNav) is the only navigation —
            // no second strip inside the page. Keyed by feature so the page
            // fade-in (.tab-pane.active > *) plays between pages of one section too.
            return (
                <div key={activeFeature.key} className="category-active-feature v2-category">
                    <div className="v2-category-body">
                        <Component />
                    </div>
                </div>
            );
        }
        return (
            <div className="category-active-feature">
                <Component />
            </div>
        );
    }

    // v2: the effect above is about to select the first feature — paint nothing
    // for that frame instead of flashing the tile grid.
    if (v2) return null;

    // Render the feature card grid
    return (
        <div className="category-grid-wrapper">
            <div className="category-grid-content">
                <div className="category-header">
                    <h2 className="category-title">{category.label}</h2>
                    {category.subtitle && (
                        <p className="category-subtitle">{category.subtitle}</p>
                    )}
                </div>
                <div className="category-grid" data-count={features.length}>
                    {features.map((feature) => (
                        <button
                            key={feature.key}
                            className="feature-card"
                            onClick={() => openFeature(feature)}
                        >
                            {/* Inline SVG component (not <img src>): ships in the JS
                                bundle so the icon paints in the same frame as the
                                title instead of popping in after a file request. */}
                            <feature.icon
                                className="feature-card-icon"
                                aria-hidden="true"
                                focusable="false"
                            />
                            <span className="feature-card-title">{feature.title}</span>
                            {feature.desc && (
                                <span className="feature-card-desc">{feature.desc}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default CategoryView;
